// src/hooks/useProgressTracking.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { createContentId } from '../utils/navigationHelpers';
import globalAudioManager from '../features/MediaLibrary/GlobalAudioManager';

export const useProgressTracking = (currentUser) => {
    const [completedUnits, setCompletedUnits] = useState({});
    const [userAnswers, setUserAnswers] = useState({});
    const [showFeedback, setShowFeedback] = useState({});
    const [visitedHotspots, setVisitedHotspots] = useState(new Set());
    const [selectedHotspot, setSelectedHotspot] = useState(null);
    const [visitedInternalPages, setVisitedInternalPages] = useState(new Set());
    const [visitedUnits, setVisitedUnits] = useState(new Set());
    const [visitedPackages, setVisitedPackages] = useState(new Set());
    const [completedNavItems, setCompletedNavItems] = useState(new Set());

    const currentHotspotAudioRef = useRef(null);

// Load user progress on mount
    useEffect(() => {
        const loadUserProgress = async () => {
            if (!currentUser) return;
            try {
                const progressRef = doc(db, 'learnerProgress', currentUser.uid);
                const progressSnapshot = await getDoc(progressRef);

                if (progressSnapshot.exists()) {
                    const data = progressSnapshot.data();
                    setCompletedUnits(data.completedUnits || {});

                    // ---------------------------------------------------------------------------
                    // userAnswers and showFeedback are session-only — never restored from
                    // Firestore on mount. This prevents wrong-answer feedback from reappearing
                    // when a learner navigates away and returns to a question.
                    //
                    // Rule:
                    //   correct → restore the saved answer so the question renders as locked
                    //   wrong   → start fresh; dot stays yellow (from completedUnits) but
                    //             the question is blank and ready to try again
                    // ---------------------------------------------------------------------------
                    const savedCompleted = data.completedUnits || {};
                    const savedAnswers   = data.userAnswers   || {};
                    const restoredAnswers = {};

                    Object.entries(savedAnswers).forEach(([unitId, answer]) => {
                        if (savedCompleted[unitId] === 'correct') {
                            restoredAnswers[unitId] = answer;
                        }
                        // wrong / dunno / still_learning → dropped intentionally
                    });

                    setUserAnswers(restoredAnswers);

                    // showFeedback: only restore for correctly answered units so the
                    // locked "Correct!" state renders without the learner clicking Check Answer
                    const restoredFeedback = {};
                    Object.keys(savedCompleted).forEach(unitId => {
                        if (savedCompleted[unitId] === 'correct') {
                            restoredFeedback[unitId] = true;
                        }
                    });
                    setShowFeedback(restoredFeedback);

                    const visitedUnitsArray = data.visitedUnits || [];
                    const visitedPagesArray = data.visitedPages || [];
                    setVisitedUnits(new Set([...visitedUnitsArray, ...visitedPagesArray]));

                    // ---------------------------------------------------------------------------
                    // Support both legacy (array) and new (map) visitedPackages formats.
                    // Legacy:  visitedPackages: ["pkgId1", "pkgId2", ...]
                    // New:     visitedPackages: { pkgId1: { catalogId, visitedAt }, ... }
                    // Local state only needs the Set of IDs in both cases.
                    // ---------------------------------------------------------------------------
                    const raw = data.visitedPackages;
                    if (Array.isArray(raw)) {
                        setVisitedPackages(new Set(raw));
                    } else if (raw && typeof raw === 'object') {
                        setVisitedPackages(new Set(Object.keys(raw)));
                    } else {
                        setVisitedPackages(new Set());
                    }

                    // Load completedNavItems — keys of the completedNavItems map
                    const rawCompleted = data.completedNavItems;
                    if (rawCompleted && typeof rawCompleted === 'object') {
                        setCompletedNavItems(new Set(Object.keys(rawCompleted)));
                    } else {
                        setCompletedNavItems(new Set());
                    }
                }
            } catch (error) {
                console.error('Error loading user progress:', error);
            }
        };

        loadUserProgress();
    }, [currentUser]);

    // ---------------------------------------------------------------------------
    // markPackageVisited
    // ---------------------------------------------------------------------------
    const markPackageVisited = useCallback(async (contentId, lessonId = null, catalogId = null) => {
        if (!currentUser || !contentId) return;

        if (visitedPackages.has(contentId)) return;

        setVisitedPackages(prev => new Set([...prev, contentId]));

        try {
            const progressRef = doc(db, 'learnerProgress', currentUser.uid);
            const snap = await getDoc(progressRef);
            const now = new Date();

            if (snap.exists()) {
                const current = snap.data();
                const raw = current.visitedPackages;

                let packagesMap = {};
                if (Array.isArray(raw)) {
                    raw.forEach(id => {
                        packagesMap[id] = { catalogId: null, visitedAt: null };
                    });
                } else if (raw && typeof raw === 'object') {
                    packagesMap = { ...raw };
                }

                if (!packagesMap[contentId]) {
                    packagesMap[contentId] = {
                        catalogId: catalogId || null,
                        visitedAt: now
                    };

                    const updates = {
                        visitedPackages: packagesMap,
                        lastVisited: now,
                        lastUpdated: now
                    };

                    if (lessonId && catalogId) {
                        updates.lastVisitedLesson = { lessonId, catalogId, timestamp: now };
                    }

                    await updateDoc(progressRef, updates);
                }
            } else {
                const packagesMap = {
                    [contentId]: {
                        catalogId: catalogId || null,
                        visitedAt: now
                    }
                };

                const newDoc = {
                    learnerId: currentUser.uid,
                    visitedPackages: packagesMap,
                    visitedUnits: [],
                    visitedPages: [],
                    completedUnits: {},
                    lastVisited: now,
                    lastUpdated: now
                };

                if (lessonId && catalogId) {
                    newDoc.lastVisitedLesson = { lessonId, catalogId, timestamp: now };
                }

                await setDoc(progressRef, newDoc);
            }
        } catch (error) {
            console.error('Error marking package as visited:', error);
        }
    }, [currentUser, visitedPackages]);

    // ---------------------------------------------------------------------------
    // markNavItemCompleted — called when all dots in a nav item are teal.
    // Writes to completedNavItems.{navItemId} = { lessonId, completedAt }
    // so CatalogView and LearnerDashboard can read lesson completion cheaply.
    // ---------------------------------------------------------------------------
    const markNavItemCompleted = useCallback(async (navItemId, lessonId) => {
        if (!currentUser || !navItemId) return;

        // Skip if already recorded
        if (completedNavItems.has(navItemId)) return;

        setCompletedNavItems(prev => new Set([...prev, navItemId]));

        try {
            const progressRef = doc(db, 'learnerProgress', currentUser.uid);
            const snap = await getDoc(progressRef);
            const now = new Date();

            const entry = { lessonId: lessonId || null, completedAt: now };

            if (snap.exists()) {
                const current = snap.data();
                const existing = current.completedNavItems || {};
                if (!existing[navItemId]) {
                    await updateDoc(progressRef, {
                        [`completedNavItems.${navItemId}`]: entry,
                        lastUpdated: now
                    });
                }
            } else {
                await setDoc(progressRef, {
                    learnerId: currentUser.uid,
                    completedNavItems: { [navItemId]: entry },
                    visitedPackages: {},
                    visitedUnits: [],
                    visitedPages: [],
                    completedUnits: {},
                    lastUpdated: now
                });
            }
        } catch (error) {
            console.error('Error marking nav item completed:', error);
        }
    }, [currentUser, completedNavItems]);

    // Mark unit as visited
    const markAsVisited = useCallback(async (contentId, pageIndex = null) => {
        if (!currentUser) return;

        try {
            const progressRef = doc(db, 'learnerProgress', currentUser.uid);
            const progressSnapshot = await getDoc(progressRef);
            const visitedId = createContentId(contentId, pageIndex);

            setVisitedUnits(prev => new Set([...prev, contentId]));
            if (pageIndex !== null) {
                setVisitedUnits(prev => new Set([...prev, createContentId(contentId, pageIndex)]));
            }

            if (progressSnapshot.exists()) {
                const current = progressSnapshot.data();
                const currentVisited = current.visitedUnits || [];
                const currentPages = current.visitedPages || [];

                if (!currentVisited.includes(contentId)) {
                    await updateDoc(progressRef, {
                        visitedUnits: [...currentVisited, contentId],
                        visitedPages: pageIndex !== null ? [...currentPages, visitedId] : currentPages,
                        lastVisited: new Date(),
                        lastUpdated: new Date()
                    });
                } else if (pageIndex !== null && !currentPages.includes(visitedId)) {
                    await updateDoc(progressRef, {
                        visitedPages: [...currentPages, visitedId],
                        lastVisited: new Date(),
                        lastUpdated: new Date()
                    });
                }
            } else {
                await setDoc(progressRef, {
                    learnerId: currentUser.uid,
                    visitedUnits: [contentId],
                    visitedPages: pageIndex !== null ? [visitedId] : [],
                    visitedPackages: {},
                    completedUnits: {},
                    lastVisited: new Date(),
                    lastUpdated: new Date()
                });
            }
        } catch (error) {
            console.error('Error marking as visited:', error);
        }
    }, [currentUser]);

    // Keep a ref to userAnswers so handleAnswerSelect and resetUnit
    // don't need it as a useCallback dependency.
    const userAnswersRef = useRef(userAnswers);
    useEffect(() => { userAnswersRef.current = userAnswers; }, [userAnswers]);
    // the latest value without needing it as a useCallback dependency.
    // This breaks the cycle: completedUnits change → new markAsCompleted →
    // UnitViewer effect re-runs → markAsCompleted called during render → warning.
    const completedUnitsRef = useRef(completedUnits);
    useEffect(() => { completedUnitsRef.current = completedUnits; }, [completedUnits]);

    // Mark unit as completed with an explicit status.
    // status can be: true (generic complete), 'correct', 'wrong',
    //                'learnt', 'dunno', 'completed', 'still_learning'
    const markAsCompleted = useCallback(async (contentId, pageIndex = null, status = true) => {
        if (!currentUser) return;

        const completionId = createContentId(contentId, pageIndex);

        // Skip if already recorded with the same status
        if (completedUnitsRef.current[completionId] === status) return;

        const newCompleted = { ...completedUnitsRef.current, [completionId]: status };
        setCompletedUnits(newCompleted);

        try {
            const progressRef = doc(db, 'learnerProgress', currentUser.uid);
            const snap = await getDoc(progressRef);

            if (snap.exists()) {
                const current = snap.data();
                await updateDoc(progressRef, {
                    completedUnits: newCompleted,
                    visitedUnits: current.visitedUnits || [],
                    visitedPages: current.visitedPages || [],
                    lastUpdated: new Date()
                });
            } else {
                await setDoc(progressRef, {
                    learnerId: currentUser.uid,
                    completedUnits: newCompleted,
                    visitedUnits: [contentId],
                    visitedPages: pageIndex !== null ? [completionId] : [],
                    visitedPackages: {},
                    lastUpdated: new Date()
                });
            }
        } catch (error) {
            console.error('Error marking as completed:', error);
        }
    }, [currentUser]);

    // Handle answer selection
    const handleAnswerSelect = useCallback(async (unitId, answer) => {
        const newAnswers = { ...userAnswersRef.current, [unitId]: answer };
        setUserAnswers(newAnswers);

        try {
            const progressRef = doc(db, 'learnerProgress', currentUser.uid);
            await updateDoc(progressRef, {
                userAnswers: newAnswers,
                lastUpdated: new Date()
            });
        } catch (error) {
            console.error('Error saving answer:', error);
        }
    }, [currentUser]);

    // Reset a unit's answer and completion status
    const resetUnit = useCallback(async (unitId) => {
    if (!currentUser || !unitId) return;

    // Clear the answer and feedback only — keep completedUnits so the
    // yellow dot stays visible while the question resets to blank
    const newAnswers = { ...userAnswersRef.current };
    delete newAnswers[unitId];
    setUserAnswers(newAnswers);

    setShowFeedback(prev => {
        const updated = { ...prev };
        delete updated[unitId];
        return updated;
    });

    try {
        const progressRef = doc(db, 'learnerProgress', currentUser.uid);
        await updateDoc(progressRef, {
            userAnswers: newAnswers,
            lastUpdated: new Date()
        });
    } catch (error) {
        console.error('Error resetting unit:', error);
    }
}, [currentUser]);

    // ---------------------------------------------------------------------------
    // checkAnswer — only reveals feedback UI.
    // It does NOT write to completedUnits here. Each renderer is responsible
    // for calling onMarkCompleted with the actual result ('correct' or 'wrong')
    // after evaluating the learner's answer. This prevents the race condition
    // where a generic `true` would overwrite the meaningful status string.
    // ---------------------------------------------------------------------------
    const checkAnswer = useCallback((unitId) => {
        setShowFeedback(prev => ({ ...prev, [unitId]: true }));
    }, []);

    // Get or create randomized options for a unit
    const randomizedOptionsRef = useRef({});

    const getRandomizedOptions = useCallback((unitId, originalOptions) => {
        if (!originalOptions || originalOptions.length === 0) return [];
        
        // Return cached version if already randomized
        if (randomizedOptionsRef.current[unitId]) {
            return randomizedOptionsRef.current[unitId];
        }

        // Shuffle
        const shuffled = [...originalOptions];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        // Store in ref — no setState, no re-render triggered
        randomizedOptionsRef.current[unitId] = shuffled;
        return shuffled;
    }, []); // no dependencies needed — ref is stable

    // Handle hotspot clicks for interactive images
    const handleHotspotClick = useCallback((hotspot) => {
        setSelectedHotspot(hotspot.id);
        setVisitedHotspots(prev => new Set([...prev, hotspot.id]));
        if (hotspot.audioUrl) playHotspotAudio(hotspot.audioUrl, hotspot.id);
    }, []);

    // Play hotspot audio
    const playHotspotAudio = useCallback((audioUrl, hotspotId) => {
        if (!audioUrl) return;

        if (currentHotspotAudioRef.current) {
            currentHotspotAudioRef.current.pause();
            currentHotspotAudioRef.current.currentTime = 0;
            currentHotspotAudioRef.current = null;
        }

        globalAudioManager.stopAll();

        if (!audioUrl.startsWith('http')) return;

        const audio = new Audio(audioUrl);
        currentHotspotAudioRef.current = audio;

        const cleanup = () => {
            if (currentHotspotAudioRef.current === audio) {
                currentHotspotAudioRef.current = null;
            }
        };

        audio.addEventListener('ended', cleanup);
        audio.addEventListener('error', cleanup);
        audio.play().catch(err => { console.error('Audio play failed:', err); cleanup(); });

        setVisitedHotspots(prev => new Set([...prev, hotspotId]));
    }, []);

    const isInternalPageVisited = useCallback((pageIndex, currentContentId) => {
        if (!currentContentId) return false;
        return visitedInternalPages.has(`${currentContentId}_internal_${pageIndex}`);
    }, [visitedInternalPages]);

    const markInternalPageVisited = useCallback((pageIndex, currentContentId) => {
        if (!currentContentId) return;
        setVisitedInternalPages(prev =>
            new Set([...prev, `${currentContentId}_internal_${pageIndex}`])
        );
    }, []);

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (currentHotspotAudioRef.current) {
                currentHotspotAudioRef.current.pause();
                currentHotspotAudioRef.current = null;
            }
            globalAudioManager.stopAll();
        };
    }, []);

    return {
        completedUnits,
        userAnswers,
        showFeedback,
        visitedHotspots,
        selectedHotspot,
        visitedInternalPages,
        visitedUnits,
        visitedPackages,
        completedNavItems,

        markAsVisited,
        markAsCompleted,
        markPackageVisited,
        markNavItemCompleted,
        handleAnswerSelect,
        resetUnit,
        checkAnswer,
        getRandomizedOptions,
        handleHotspotClick,
        playHotspotAudio,
        isInternalPageVisited,
        markInternalPageVisited,

        setShowFeedback,
        setVisitedHotspots,
        setSelectedHotspot
    };
};